import React, { useState, useEffect } from 'react';
import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Box,
    Tabs,
    Tab,
    Chip,
    TablePagination,
    Breadcrumbs,
    Link as MuiLink
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { TrainingService, IFrame, IClip, ITrainingFilters } from '../../service/training.service';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export const TrainingDataManager = () => {
    const [tabValue, setTabValue] = useState(0);
    const [frames, setFrames] = useState<IFrame[]>([]);
    const [clips, setClips] = useState<IClip[]>([]);
    const [filters, setFilters] = useState<ITrainingFilters>({
        page: 1,
        pageSize: 50
    });
    const [washparks, setWashparks] = useState<string[]>([]);
    const [cameras, setCameras] = useState<string[]>([]);
    const [stats, setStats] = useState<any>(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalFrames, setTotalFrames] = useState(0);
    const [totalClips, setTotalClips] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        loadData();
        loadStats();
    }, [filters, page, pageSize]);

    const loadData = async () => {
        try {
            const framesData = await TrainingService.getFrames({
                ...filters,
                page,
                pageSize
            });
            const clipsData = await TrainingService.getClips({
                ...filters,
                page,
                pageSize
            });
            
            const framesList = framesData?.data || [];
            const clipsList = clipsData?.data || [];
            
            setFrames(framesList);
            setClips(clipsList);
            setTotalFrames(framesData?.total || 0);
            setTotalClips(clipsData?.total || 0);

            const uniqueWashparks = Array.from(new Set([
                ...framesList.map(item => item.washpark),
                ...clipsList.map(item => item.washpark)
            ])).filter(Boolean);

            const uniqueCameras = Array.from(new Set([
                ...framesList.map(item => item.camera),
                ...clipsList.map(item => item.camera)
            ])).filter(Boolean);

            setWashparks(uniqueWashparks);
            setCameras(uniqueCameras);
        } catch (error) {
            console.error("Fehler beim Laden der Daten:", error);
            setFrames([]);
            setClips([]);
        }
    };

    const loadStats = async () => {
        try {
            const statsData = await TrainingService.getTrainingStats();
            setStats(statsData);
        } catch (error) {
            console.error("Fehler beim Laden der Statistiken:", error);
        }
    };

    const handleFilterChange = (field: keyof ITrainingFilters, value: any) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleDeleteFrame = async (frameId: string) => {
        if (window.confirm('Möchten Sie diesen Frame wirklich löschen?')) {
            try {
                await TrainingService.deleteFrame(frameId);
                loadData();
            } catch (error) {
                console.error("Fehler beim Löschen des Frames:", error);
            }
        }
    };

    const handleDeleteClip = async (clipId: string) => {
        if (window.confirm('Möchten Sie diesen Clip wirklich löschen?')) {
            try {
                await TrainingService.deleteClip(clipId);
                loadData();
            } catch (error) {
                console.error("Fehler beim Löschen des Clips:", error);
            }
        }
    };

    const handleUpdateAnnotation = async (frameId: string, hasAnnotation: boolean) => {
        try {
            await TrainingService.updateFrameAnnotation(frameId, hasAnnotation);
            loadData();
        } catch (error) {
            console.error("Fehler beim Aktualisieren der Annotation:", error);
        }
    };

    const handleUpdatePriority = async (clipId: string, priority: boolean, reason?: string) => {
        try {
            await TrainingService.updateClipPriority(clipId, priority, reason);
            loadData();
        } catch (error) {
            console.error("Fehler beim Aktualisieren der Priorität:", error);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Breadcrumbs Navigation */}
            <Breadcrumbs aria-label="Navigationspfad" sx={{ mb: 2 }}>
                <MuiLink 
                    color="inherit" 
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/dashboard')}
                    sx={{ textDecoration: 'none' }}
                >
                    Dashboard
                </MuiLink>
                <MuiLink 
                    color="inherit" 
                    component="button"
                    variant="body1"
                    onClick={() => navigate('/backoffice')}
                    sx={{ textDecoration: 'none' }}
                >
                    Administration
                </MuiLink>
                <Typography color="text.primary">Trainingsdaten-Verwaltung</Typography>
            </Breadcrumbs>
            
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton 
                        sx={{ mr: 2 }}
                        onClick={() => navigate('/backoffice')}
                        aria-label="Zurück zur Administration"
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" component="h1">
                        Trainingsdaten-Verwaltung
                    </Typography>
                </Box>
            </Box>

            {/* Statistiken */}
            {stats && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6">Frames Gesamt</Typography>
                                <Typography variant="h4">{stats.totalFrames}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6">Annotierte Frames</Typography>
                                <Typography variant="h4">{stats.annotatedFrames}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6">Clips Gesamt</Typography>
                                <Typography variant="h4">{stats.totalClips}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6">Prioritäts-Clips</Typography>
                                <Typography variant="h4">{stats.priorityClips}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Filter */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Waschpark</InputLabel>
                                <Select
                                    value={filters.washpark || ''}
                                    onChange={(e) => handleFilterChange('washpark', e.target.value)}
                                >
                                    <MenuItem value="">Alle</MenuItem>
                                    {washparks.map(wp => (
                                        <MenuItem key={wp} value={wp}>{wp}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Kamera</InputLabel>
                                <Select
                                    value={filters.camera || ''}
                                    onChange={(e) => handleFilterChange('camera', e.target.value)}
                                >
                                    <MenuItem value="">Alle</MenuItem>
                                    {cameras.map(cam => (
                                        <MenuItem key={cam} value={cam}>{cam}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={filters.hasAnnotation || false}
                                        onChange={(e) => handleFilterChange('hasAnnotation', e.target.checked)}
                                    />
                                }
                                label="Nur annotierte Frames"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={filters.priority || false}
                                        onChange={(e) => handleFilterChange('priority', e.target.checked)}
                                    />
                                }
                                label="Nur Prioritäts-Clips"
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Tabs für Frames und Clips */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                    <Tab label="Frames" />
                    <Tab label="Clips" />
                </Tabs>
            </Box>

            {/* Frames Tab */}
            <TabPanel value={tabValue} index={0}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Waschpark</TableCell>
                                <TableCell>Kamera</TableCell>
                                <TableCell>Dateiname</TableCell>
                                <TableCell>Index</TableCell>
                                <TableCell>Annotation</TableCell>
                                <TableCell>Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {frames.map((frame) => (
                                <TableRow key={frame._id}>
                                    <TableCell>{frame.washpark}</TableCell>
                                    <TableCell>{frame.camera}</TableCell>
                                    <TableCell>{frame.filename}</TableCell>
                                    <TableCell>{frame.index}</TableCell>
                                    <TableCell>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={frame.hasAnnotation}
                                                    onChange={(e) => handleUpdateAnnotation(frame._id, e.target.checked)}
                                                />
                                            }
                                            label={frame.hasAnnotation ? "Annotiert" : "Nicht annotiert"}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleDeleteFrame(frame._id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalFrames}
                        page={page - 1}
                        onPageChange={(_, newPage) => setPage(newPage + 1)}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={(event) => {
                            setPageSize(parseInt(event.target.value, 10));
                            setPage(1);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="Einträge pro Seite"
                    />
                </TableContainer>
            </TabPanel>

            {/* Clips Tab */}
            <TabPanel value={tabValue} index={1}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Waschpark</TableCell>
                                <TableCell>Kamera</TableCell>
                                <TableCell>Dateiname</TableCell>
                                <TableCell>Priorität</TableCell>
                                <TableCell>Grund</TableCell>
                                <TableCell>Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {clips.map((clip) => (
                                <TableRow key={clip._id}>
                                    <TableCell>{clip.washpark}</TableCell>
                                    <TableCell>{clip.camera}</TableCell>
                                    <TableCell>{clip.filename}</TableCell>
                                    <TableCell>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={clip.priority}
                                                    onChange={(e) => handleUpdatePriority(clip._id, e.target.checked, clip.reason)}
                                                />
                                            }
                                            label={clip.priority ? "Priorität" : "Normal"}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            value={clip.reason || ''}
                                            onChange={(e) => handleUpdatePriority(clip._id, clip.priority, e.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleDeleteClip(clip._id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalClips}
                        page={page - 1}
                        onPageChange={(_, newPage) => setPage(newPage + 1)}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={(event) => {
                            setPageSize(parseInt(event.target.value, 10));
                            setPage(1);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="Einträge pro Seite"
                    />
                </TableContainer>
            </TabPanel>
        </Container>
    );
};

export default TrainingDataManager; 